import { useState } from "react";
import { AccionesUsuarioCabecera, FilaVerComoMovil } from "@/components/admin/AccionesUsuarioCabecera";
import { usePublicarVerComoInmobiliaria } from "@/contexts/InmobiliariaImpersonationContext";
import { TabBarPlano } from "@/components/ui/tab-bar";
import { KpiCardUniforme } from "@/components/ui/kpi-card";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, BarChart3, UserSearch,
  Calendar, DollarSign, FileText, LucideIcon, Percent,
  Building2, BarChart2, CalendarDays, UserCheck, Menu, UserCog,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { useInmobContexto } from "@/hooks/useInmobiliariaPersonaId";
import { useInmobiliariaImpersonation } from "@/contexts/InmobiliariaImpersonationContext";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { InmobiliariaImpersonationSelector } from "./InmobiliariaImpersonationSelector";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { usePortalNavEstado } from "@/hooks/usePortalNav";

const PORTAL_INMOB_MENU_ID = 17;

const iconMap: Record<string, LucideIcon> = {
  "/admin/portal-inmobiliaria/dashboard": LayoutDashboard,
  "/admin/portal-inmobiliaria/agentes": Users,
  "/admin/portal-inmobiliaria/pipeline": BarChart2,
  "/admin/portal-inmobiliaria/prospectos": UserCheck,
  "/admin/portal-inmobiliaria/citas": CalendarDays,
  "/admin/portal-inmobiliaria/comisiones": DollarSign,
  "/admin/portal-inmobiliaria/reportes": BarChart3,
  "/admin/portal-inmobiliaria/equipo": UserCog,
  "/admin/portal-inmobiliaria/cuenta": UserCog,
};


export const PortalInmobiliariaLayout = () => {
  // Solo mientras este layout está montado se ve como el usuario (y en solo lectura).
  usePublicarVerComoInmobiliaria();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const canImpersonate = profile?.puede_impersonar === true;
  const { contexto } = useInmobContexto();
  const { isImpersonating, impersonatedRolId } = useInmobiliariaImpersonation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Nombre y comisión de la inmobiliaria del portal (la del impersonado en "ver como"): los
  // resuelve el servidor con `get_inmob_contexto`, con el mismo permiso que el resto del portal.
  const agencyName = contexto?.nombre || "Mi Inmobiliaria";
  const comisionPct = contexto?.comision_pct ?? null;

  const { items: todasLasTabs, cargando: navCargando } = usePortalNavEstado(PORTAL_INMOB_MENU_ID, iconMap, LayoutDashboard);
  // Menú por permiso: solo las pantallas con `leer` para el rol real (Super Admin: todas).
  const { allowedPaths, isSuperAdmin, isLoading: permisosCargando } = useAllowedMenus();
  // En "ver como" se pinta el menú del impersonado, sin abrir nada que el rol real no tenga:
  // intersección de las dos listas de `leer`.
  const { data: rutasImpersonado, isPending: rutasPendientes } = useQuery({
    queryKey: ["inmob-rutas-impersonado", impersonatedRolId],
    enabled: isImpersonating && !!impersonatedRolId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Set<string>> => {
      // `submenus_permisos` no tiene FK a `submenus`: dos lecturas.
      const { data: permisos, error } = await (supabase as any)
        .from("submenus_permisos")
        .select("submenu_id, permisos!inner(nombre)")
        .eq("rol_id", impersonatedRolId)
        .eq("activo", true)
        .eq("permisos.nombre", "leer");
      if (error) throw error;
      const ids = ((permisos ?? []) as any[]).map((r) => r.submenu_id);
      if (ids.length === 0) return new Set();
      const { data: submenus, error: e2 } = await (supabase as any)
        .from("submenus")
        .select("vista_front_end")
        .in("id", ids)
        .eq("activo", true)
        .like("vista_front_end", "/admin/portal-inmobiliaria/%");
      if (e2) throw e2;
      return new Set(((submenus ?? []) as any[]).map((r) => r.vista_front_end as string).filter(Boolean));
    },
  });
  const tabs = todasLasTabs.filter((t) =>
    (isSuperAdmin || allowedPaths.has(t.path)) &&
    (!isImpersonating || (rutasImpersonado?.has(t.path) ?? false)),
  );

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const showBackButton = canReturnToAdmin;

  const currentSection = tabs.find(t => isActive(t.path))?.label || "";

  // Perfil: con "Ver como", la persona impersonada (nombre y rol suyos), no el admin.
  const perfil = usePerfilVista({ rol: profile?.rol_nombre ?? "Inmobiliaria" });
  const userName = perfil.nombre.split(/\s+/).slice(0, 2).join(" ");
  const userRole = perfil.rol || "Inmobiliaria";
  const initials = perfil.iniciales;

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const sidebar = (
    <PortalSidebar
      portal="Portal Inmobiliaria"
      miCuenta
      cargando={navCargando || permisosCargando || (isImpersonating && !!impersonatedRolId && rutasPendientes)}
      contexto={
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60 leading-4">Inmobiliaria</p>
          <p className="text-[13px] font-semibold leading-5 text-foreground truncate">{agencyName}</p>
          {comisionPct !== null && comisionPct !== undefined && (
            <div className="flex items-center gap-1 leading-4">
              <Percent className="h-3 w-3 text-primary" />
              <span className="text-[12px] font-medium text-primary">Comisión: {comisionPct.toFixed(2)}%</span>
            </div>
          )}
        </div>
      }
      groups={[{
        items: tabs.map((tab) => ({
          key: tab.path,
          label: tab.label,
          icon: tab.icon,
          active: isActive(tab.path),
          onSelect: () => handleNavigate(tab.path),
        })),
      }]}
      usuario={{
        nombre: userName,
        rol: userRole,
        iniciales: initials,
      }}
      onRegresar={showBackButton ? () => navigate("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <PortalTrackingProvider portal="inmobiliarias">
      <div className="inmob-portal min-h-screen flex antialiased overflow-x-clip">
        {/* Desktop sidebar */}
        <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
          {sidebar}
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
            {sidebar}
          </SheetContent>
        </Sheet>

        <div className="flex-1 lg:pl-64 min-w-0 overflow-x-clip">
          {/* Desktop header */}
          <header className="hidden lg:flex sticky top-0 z-20 h-16 items-center gap-4 px-6 lg:px-8 bg-card border-b border-border-soft">
            {/* Título del menú activo, como en Cobranza: las páginas no pintan su propio h1. */}
            <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-foreground tracking-tight">{currentSection}</h1>
            <AccionesUsuarioCabecera verComo={canImpersonate ? <InmobiliariaImpersonationSelector /> : undefined} />
          </header>

          {/* Mobile header */}
          <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
            <div className="flex items-center px-4 pt-3 pb-2 gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-1.5 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
              </div>
              <AccionesUsuarioCabecera compacto />
            </div>
            {canImpersonate && (
              <FilaVerComoMovil>
                <InmobiliariaImpersonationSelector />
              </FilaVerComoMovil>
            )}
          </header>
          <main className="px-4 py-5 sm:px-8 bg-background min-h-[calc(100vh-64px)]">
            {/* Pestañas y tarjetas KPI uniformes en todo el portal (mismo criterio que Cobranza). */}
            <TabBarPlano>
              <KpiCardUniforme>
                <Outlet />
              </KpiCardUniforme>
            </TabBarPlano>
          </main>
        </div>
      </div>
    </PortalTrackingProvider>
  );
};
